import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { ICON } from '@core/util/icon';
import { BusinessUser, BusinessUserRole } from '@models/businesses';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { BaseComponent } from '@core/base.component';
import { STATUSES } from '@models/statuses';
import { Select, Store } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { Revoke } from '@state/llc/llc.actions';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { CloserSyncSignatureRequestModalComponent } from './closer-sync-signature-request-modal/closer-sync-signature-request-modal.component';
import { ConnectMainModalComponent } from './steps/connect-main-modal.component';
import { WelcomeModalComponent } from './welcome-modal/welcome-modal.component';
import { CloserSyncService } from '@services/closer-sync/closer-sync.service';
import { ISyncStatuses } from './closer-sync-integration.models';
import { initializeSyncStatus } from './closer-sync-integration.util';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';

enum ACTIONS {
	REVOKE = 'revoke',
}

export enum CLOSER_SYNC_DRAWER_VIEW {
	ACTIONS_REQUIRED = 'actions-required',
	SETTINGS = 'settings',
	DISCONNECT_ASSYST = 'disconnect-assyst',
}

enum COLUMNS {
	ACTION = 'action',
	NAME = 'name',
	STATUS = 'status',
	LENDERS = 'lenders',
}

interface RowAction {
	action: ACTIONS;
	id: string;
}

interface CloserSyncConnectedUser {
	userId: string;
	syncStatus: ISyncStatuses;
	name: string;
	lenders?: {
		numOfDisconnectedLenders: number;
		numOfConnectedLenders: number;
		numOfContactingLenders: number;
		numOfActionsRequiredLenders: number;
	};
	user: BusinessUser;
}

interface VM {
	currentUser: BusinessUser;
	shouldRenderActionsRequiredBackButton: boolean;
	connectedUsers: CloserSyncConnectedUser[];
	currentDrawerView: CLOSER_SYNC_DRAWER_VIEW;
	lawyers: BusinessUser[];
	// doesAnyUserHaveActionsRequired: boolean;
	// usersMortgageInstitutions: { [id: string]: UserMortgageInstitution[] }; // DO NOT REMOVE THIS COMMENTED CODE
}

const defaultVM: VM = {
	currentUser: null,
	shouldRenderActionsRequiredBackButton: false,
	connectedUsers: [],
	currentDrawerView: null,
	lawyers: [],
	// doesAnyUserHaveActionsRequired: false,
	// usersMortgageInstitutions: {}, // DO NOT REMOVE THIS COMMENTED CODE
};

const STATUS_PRIORITY: ISyncStatuses[] = [
	STATUSES.CONNECTED,
	STATUSES.ACTIONS_REQUIRED,
	STATUSES.REQUIRES_PIN,
	STATUSES.REQUIRES_CONSENT,
	STATUSES.REQUIRES_SIGNATURE,
	STATUSES.DISCONNECTED,
];

@UntilDestroy()
@Component({
	selector: 'app-closer-sync',
	templateUrl: './closer-sync.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncComponent
	extends BaseComponent<VM>
	implements OnInit, AfterViewInit
{
	public isFullyClosed = true;
	public NUM_OF_LENDERS = 10;
	public openConnect = false;
	@ViewChild('drawer') drawer: MatDrawer;
	@Select(BusinessUsersState.getLawyers) lawyers$: Observable<BusinessUser[]>;
	@Select(BusinessUsersState.getLoggedInBusinessUser)
	currentUser$: Observable<BusinessUser>;
	@SelectSnapshot(BusinessUsersState.getSyncLawyersRatio)
	syncLawyersRatio: string;
	@SelectSnapshot(BusinessUsersState.isSyncIntegrationConnected)
	isSyncIntegrationConnected: boolean;

	ACTIONS = ACTIONS;
	COLUMNS = COLUMNS;
	displayedColumns: COLUMNS[] = [COLUMNS.NAME, COLUMNS.STATUS, COLUMNS.ACTION];
	// displayedColumns: COLUMNS[] = [COLUMNS.NAME, COLUMNS.STATUS, COLUMNS.LENDERS, COLUMNS.ACTION]; // DO NOT REMOVE THIS COMMENTED CODE
	ICON = ICON;
	STATUSES = STATUSES;
	CLOSER_SYNC_DRAWER_VIEW = CLOSER_SYNC_DRAWER_VIEW;

	@Input() benefits: string[] = [
		'Mortgage and lender details auto-populate into your Closer files and mortgage documents.',
		'Streamlined submission of your lender forms including RFF & Final Reports.',
	];

	profileUrl$!: Observable<string>;

	public showSyncSuccessState = false;

	constructor(
		private store: Store,
		private matDialog: MatDialog,
		private closerSyncService: CloserSyncService,
		private route: ActivatedRoute,
		private router: Router,
	) {
		super(defaultVM);
	}

	rowActionClick(action: RowAction) {
		switch (action?.action) {
			case ACTIONS.REVOKE:
				this.store.dispatch(new Revoke(action.id));
		}
	}

	ngAfterViewInit(): void {
		combineLatest([this.route.queryParams, this.currentUser$])
			.pipe(
				untilDestroyed(this),
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				filter(([params, currentUser]) => !!currentUser),
				take(1),
			)
			.subscribe(([params, currentUser]) => {
				const settings = params['settings'];
				const connect = params['connect'];
				if (connect) {
					this.matDialog.closeAll();
					this.connect(
						currentUser.id === connect
							? currentUser
							: this.VM.lawyers.find((lawyer) => lawyer.id === connect),
						STATUSES.DISCONNECTED,
					);
				}
				if (settings === 'open') {
					setTimeout(() => {
						this.openLendersDrawer();
					});
				}
			});
	}

	ngOnInit(): void {
		combineLatest([this.lawyers$, this.currentUser$])
			.pipe(
				untilDestroyed(this),
				tap(([lawyers]) => {
					this.VM = { lawyers };
				}),
				map(([users, currentUser]) => {
					let currentUserIndex: number;

					const mappedUsers = users?.map((user, index) => {
						if (user.id === currentUser?.id) {
							currentUserIndex = index;
						}
						return {
							user,
							userId: user.id,
							syncStatus: initializeSyncStatus(user),
							name: `${user.firstName} ${user.lastName}`,
						};
					});
					const currentMappedUser =
						currentUserIndex > -1
							? mappedUsers.splice(currentUserIndex, 1)[0]
							: null;
					const connectedUsers = mappedUsers.sort(this.sortSyncStatuses);
					if (currentMappedUser) {
						connectedUsers.unshift(currentMappedUser);
					}
					return { connectedUsers, currentUser };
				}),
			)
			.subscribe(({ connectedUsers, currentUser }) => {
				this.VM = { connectedUsers };
				if (currentUser?.id !== this.VM.currentUser?.id) {
					this.VM = { currentUser };
					this.openIntroModal(currentUser);
				}
			});

		// DO NOT REMOVE THIS COMMENTED CODE
		// combineLatest([this.lawyers$, this.userMortgageInstitutionRepoService.All()])
		// 	.pipe(untilDestroyed(this))
		// 	.subscribe(([users, userInstitutions]) => {
		// 		if (!this.users || this.users?.length === 0) {
		// 			this.connectedUsers$.next([]);
		// 		}

		// 		const usersMortgageInstitutions = userInstitutions.reduce(
		// 			(acc, curr) => {
		// 				if (!acc[curr.userId]) {
		// 					acc[curr.userId] = [curr];
		// 				} else {
		// 					acc[curr.userId].push(curr);
		// 				}
		// 				return acc;
		// 			},
		// 			{} as Record<string, UserMortgageInstitution[]>,
		// 		);

		// 		this.doesAnyUserHaveActionsRequired = false;
		// 		this.lendersWithActionsRequired = [];

		// 		const usersVM = users
		// 			.map((user: BusinessUser) => {
		// 				const status = user.closerSyncId
		// 					? user.signature
		// 						? user.accessPin
		// 							? STATUSES.CONNECTED
		// 							: STATUSES.REQUIRES_PIN
		// 						: STATUSES.REQUIRES_SIGNATURE
		// 					: STATUSES.DISCONNECTED;
		// 				const name = `${user.firstName} ${user.lastName}`;
		// 				const lenders =
		// 					status === STATUSES.CONNECTED ? this.transformLendersToStatus(usersMortgageInstitutions[user.id] ?? []) : null;

		// 				return {
		// 					user,
		// 					userId: user.id,
		// 					status,
		// 					name,
		// 					lenders,
		// 				};
		// 			})
		// 			.sort((a, b) => {
		// 				if (a.name < b.name) {
		// 					return -1;
		// 				}
		// 				if (a.name > b.name) {
		// 					return 1;
		// 				}
		// 				return 0;
		// 			});

		// 		this.connectedUsers$.next(usersVM);
		// 	});
	}

	get closerSyncDescription(): string {
		return this.closerSyncService.CLOSER_SYNC_DESCRIPTION;
	}

	closeDrawer() {
		this.drawer.close();
		setTimeout(() => {
			this.isFullyClosed = true;
			this.VM = { currentDrawerView: null };
			this.showSyncSuccessState = false;

			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { settings: null },
				queryParamsHandling: 'merge',
			});
		}, 300);
	}

	openDrawer() {
		this.drawer.open();
		this.isFullyClosed = false;
	}

	openLendersDrawer() {
		this.VM = { currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.SETTINGS };
		this.openDrawer();

		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { settings: 'open' },
			queryParamsHandling: 'merge',
		});
	}

	openDisconnectAssystDrawer() {
		this.VM = { currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.DISCONNECT_ASSYST };
		this.openDrawer();
	}

	openActionsRequiredDrawer() {
		this.VM = { currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.ACTIONS_REQUIRED };
		this.VM = { shouldRenderActionsRequiredBackButton: false };
		this.openDrawer();
	}

	/**
	 *
	 * @param user BusinessUser
	 *
	 * @description If the current user has clicked on a connect button of a lawyer's name other than their own, it will open the send signature request modal
	 * Otherwise, they will be taken through the auth flow
	 */
	connect(user: BusinessUser, syncStatus: ISyncStatuses) {
		if (!this.isUserCurrentUser(user)) {
			this.openRequestCloserSyncSignatureModal(user);
			return;
		}

		if (syncStatus === STATUSES.ACTIONS_REQUIRED) {
			this.openDisconnectAssystDrawer();
			return;
		}

		this.closerSyncConnect();
	}

	isUserCurrentUser(user: BusinessUser) {
		return user.id === this.VM.currentUser?.id;
	}

	disableDrawerButton(user: BusinessUser) {
		const isNotTheUser = !this.isUserCurrentUser(user);
		const isAdmin = this.VM.currentUser?.admin;

		return isNotTheUser && !isAdmin;
	}

	// DO NOT REMOVE THIS COMMENTED CODE
	// private transformLendersToStatus(lenders: UserMortgageInstitution[]) {
	// 	let numOfDisconnectedLenders = this.infoTrackInstitutions?.length;
	// 	let numOfConnectedLenders = 0;
	// 	let numOfContactingLenders = 0;
	// 	let numOfActionsRequiredLenders = 0;
	// 	let numOfRequestDisconnectLenders = 0;

	// 	for (const lender of lenders) {
	// 		switch (lender.status) {
	// 			case STATUSES.CONNECTED:
	// 				numOfConnectedLenders++;
	// 				numOfDisconnectedLenders--;
	// 				break;
	// 			case STATUSES.CONTACTING_LENDER:
	// 				numOfContactingLenders++;
	// 				numOfDisconnectedLenders--;
	// 				break;
	// 			case STATUSES.ACTIONS_REQUIRED:
	// 				numOfActionsRequiredLenders++;
	// 				numOfDisconnectedLenders--;
	// 				this.doesAnyUserHaveActionsRequired = true;
	// 				if (!this.lendersWithActionsRequired?.some((x) => x.id === lender.id)) {
	// 					this.lendersWithActionsRequired.push(lender);
	// 				}
	// 				break;
	// 			case STATUSES.REQUEST_DISCONNECT:
	// 				numOfRequestDisconnectLenders++;
	// 				numOfDisconnectedLenders--;
	// 				break;
	// 		}
	// 	}

	// 	return {
	// 		numOfDisconnectedLenders,
	// 		numOfConnectedLenders,
	// 		numOfContactingLenders,
	// 		numOfActionsRequiredLenders,
	// 		numOfRequestDisconnectLenders,
	// 	};
	// }

	public closeInstitutionsDrawer(callingComponent: CLOSER_SYNC_DRAWER_VIEW) {
		switch (callingComponent) {
			case CLOSER_SYNC_DRAWER_VIEW.SETTINGS: {
				this.VM = {
					currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.ACTIONS_REQUIRED,
				};
				this.VM = { shouldRenderActionsRequiredBackButton: true };
				break;
			}
			case CLOSER_SYNC_DRAWER_VIEW.ACTIONS_REQUIRED:
				this.VM = { currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.SETTINGS };
				break;
			default:
				this.closeDrawer();
				break;
		}
	}

	private openConnectMainModal() {
		const dialogRef = this.matDialog.open(ConnectMainModalComponent, {
			width: '750px',
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe((openInstructions) => {
				if (openInstructions) {
					this.VM = {
						currentDrawerView: CLOSER_SYNC_DRAWER_VIEW.DISCONNECT_ASSYST,
					};
					this.openDrawer();
				}
			});
	}

	private openRequestCloserSyncSignatureModal(user: BusinessUser) {
		this.matDialog.open(CloserSyncSignatureRequestModalComponent, {
			width: '650px',
			data: { currentUser: this.VM.currentUser, user },
		});
	}

	private closerSyncConnect() {
		this.openConnectMainModal();
	}

	private sortSyncStatuses(
		a: CloserSyncConnectedUser,
		b: CloserSyncConnectedUser,
	) {
		return (
			STATUS_PRIORITY.indexOf(a.syncStatus) -
			STATUS_PRIORITY.indexOf(b.syncStatus)
		);
	}

	private openIntroModal(user: BusinessUser) {
		if (!user?.closerSyncId && user?.role === BusinessUserRole.LAWYER) {
			this.closerSyncConnect();
		} else if (
			localStorage.getItem('dismissed-closer-sync-welcome-modal') !== 'true' &&
			user?.role !== BusinessUserRole.LAWYER
		) {
			this.matDialog.open(WelcomeModalComponent, { width: '750px' });
		}
	}

	openHelpDrawer() {
		this.showSyncSuccessState = true;
		this.openDisconnectAssystDrawer();
	}
}
